import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSidebarItems, flattenSidebarItemsForSearch } from './config/SidebarItems';
import { menuLabel, viewLabel } from '../../i18n/labels';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { search, SearchResponse } from '../../services/searchService';
import {
  GLOBAL_SEARCH_MODULE_LABELS,
  flattenSearchResults,
  getOrderedSearchModules,
  type FlatSearchRecord,
} from '../../config/globalSearch';
import {
  GLOBAL_SEARCH_TYPE_ICONS,
  GLOBAL_SEARCH_DEFAULT_ICON,
} from '../../config/globalSearchIcons';

type PageItem = {
  kind: 'page';
  path: string;
  label: string;
  group?: string;
  icon?: React.ReactNode;
};

type RecordItem = {
  kind: 'record';
  moduleKey: string;
  path: string;
  label: string;
  subtitle?: string;
  typeLabel: string;
};

type CombinedItem = PageItem | RecordItem;

type DropdownRow =
  | { kind: 'section'; label: string; id: string }
  | { kind: 'item'; item: CombinedItem; index: number };

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const CombinedSearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [recordResults, setRecordResults] = useState<SearchResponse>({});
  const [recordsLoading, setRecordsLoading] = useState(false);
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user, can, permissionsLoaded } = useAuth();
  const canView = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'view') : undefined),
    [can, permissionsLoaded, user?.role],
  );
  const canEdit = useMemo(
    () => (permissionsLoaded || user?.role === 'ADMIN' ? (moduleKey: string) => can(moduleKey, 'edit') : undefined),
    [can, permissionsLoaded, user?.role],
  );
  const { t } = useTranslation('sidebar');
  const flatItems = useMemo(() => {
    const items = getSidebarItems(user?.role, canView, canEdit);
    return flattenSidebarItemsForSearch(items, undefined, {
      menu: (name) => menuLabel(t, name),
      view: (label) => viewLabel(t, label),
    });
  }, [user?.role, canView, canEdit, t]);

  const filteredPages = useMemo(() => {
    if (!query.trim()) return flatItems.slice(0, 8);
    const q = query.trim().toLowerCase();
    return flatItems.filter(
      (entry) => entry.label.toLowerCase().includes(q) || (entry.group && entry.group.toLowerCase().includes(q)),
    );
  }, [flatItems, query]);

  useEffect(() => {
    if (!debouncedQuery) {
      setRecordResults({});
      setRecordsLoading(false);
      return;
    }
    let cancelled = false;
    setRecordsLoading(true);
    search(debouncedQuery, undefined, 8)
      .then((data) => {
        if (!cancelled) {
          setRecordResults(data);
          setRecordsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRecordResults({});
          setRecordsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const pageItems = useMemo<PageItem[]>(
    () =>
      filteredPages.map((p) => ({
        kind: 'page' as const,
        path: p.path,
        label: p.label,
        group: p.group,
        icon: p.icon,
      })),
    [filteredPages],
  );

  const recordItems = useMemo<RecordItem[]>(
    () =>
      flattenSearchResults(recordResults).map((r: FlatSearchRecord) => ({
        kind: 'record' as const,
        moduleKey: r.moduleKey,
        path: r.path,
        label: r.label,
        subtitle: r.subtitle,
        typeLabel: r.typeLabel,
      })),
    [recordResults],
  );

  const orderedRecordModules = useMemo(() => getOrderedSearchModules(recordResults), [recordResults]);

  const dropdownRows = useMemo<DropdownRow[]>(() => {
    const rows: DropdownRow[] = [];
    let index = 0;

    if (pageItems.length > 0) {
      rows.push({ kind: 'section', label: 'Pages', id: 'section-pages' });
      for (const item of pageItems) {
        rows.push({ kind: 'item', item, index });
        index += 1;
      }
    }

    for (const moduleKey of orderedRecordModules) {
      const moduleRecords = recordItems.filter((r) => r.moduleKey === moduleKey);
      if (moduleRecords.length === 0) continue;
      rows.push({
        kind: 'section',
        label: GLOBAL_SEARCH_MODULE_LABELS[moduleKey] || moduleKey,
        id: `section-${moduleKey}`,
      });
      for (const item of moduleRecords) {
        rows.push({ kind: 'item', item, index });
        index += 1;
      }
    }

    return rows;
  }, [pageItems, recordItems, orderedRecordModules]);

  const selectableRows = useMemo(
    () => dropdownRows.filter((row): row is Extract<DropdownRow, { kind: 'item' }> => row.kind === 'item'),
    [dropdownRows],
  );

  const selectPath = useCallback(
    (path: string) => {
      navigate(path);
      setQuery('');
      setOpen(false);
      setHighlightIndex(0);
      setRecordResults({});
    },
    [navigate],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = selectableRows[highlightIndex];
      if (row) {
        selectPath(row.item.path);
      }
      return;
    }
    if (!open) return;
    if (selectableRows.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % selectableRows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + selectableRows.length) % selectableRows.length);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlightIndex(0);
    }
  };

  useEffect(() => {
    setHighlightIndex(0);
    setOpen(query.length > 0);
  }, [query]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [recordResults, filteredPages]);

  useEffect(() => {
    if (open && listRef.current && selectableRows.length) {
      const active = selectableRows[highlightIndex];
      if (!active) return;
      const el = listRef.current.querySelector(`[data-index="${active.index}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIndex, open, selectableRows]);

  useEffect(() => {
    const handleClickOutside = (ev: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleGlobalShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        if (query.length > 0) setHighlightIndex(0);
      }
    };
    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, [query.length]);

  const hasContent = selectableRows.length > 0 || recordsLoading;
  const showDropdown = open && (hasContent || query.trim().length > 0);

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.length > 0 && setOpen(true)}
          placeholder="Search pages, emails, templates…"
          className={cn(
            'w-full pl-9 py-2 rounded-lg text-sm border transition-colors',
            query.length > 0 ? 'pr-9' : 'pr-4',
            colors.surface,
            colors.border,
            colors.text,
            'placeholder:text-gray-400 dark:placeholder:text-gray-500',
            'focus:outline-none focus:ring-2 focus:border-transparent',
            colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : 'focus:ring-indigo-500',
          )}
          aria-label="Search pages, emails, templates, and records"
          aria-expanded={open}
          aria-controls="combined-search-listbox"
          aria-autocomplete="list"
          role="combobox"
        />
        {query.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setOpen(false);
              setHighlightIndex(0);
              setRecordResults({});
              inputRef.current?.focus();
            }}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors',
              colors.textMuted,
              'hover:bg-black/5 dark:hover:bg-white/5 hover:text-gray-700 dark:hover:text-gray-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-1',
              colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : 'focus:ring-indigo-500',
            )}
            aria-label="Clear search"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {showDropdown && (
        <div
          ref={listRef}
          id="combined-search-listbox"
          role="listbox"
          className={cn(
            'absolute left-0 right-0 top-full mt-1 max-h-80 overflow-auto rounded-lg border shadow-lg z-50',
            colors.surface,
            colors.border,
          )}
        >
          {!hasContent && !recordsLoading ? (
            <div className={cn('px-4 py-3 text-sm', colors.textMuted)}>
              {query.trim() ? 'No pages or records match.' : 'Type to search pages, emails, templates, and records.'}
            </div>
          ) : (
            <>
              {dropdownRows.map((row) => {
                if (row.kind === 'section') {
                  return (
                    <div
                      key={row.id}
                      className={cn('px-3 pt-2 pb-1 sticky top-0 z-10', colors.surface, colors.border, 'border-b')}
                    >
                      <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
                        {row.label}
                      </p>
                    </div>
                  );
                }

                const { item, index } = row;
                const isActive = selectableRows[highlightIndex]?.index === index;

                if (item.kind === 'page') {
                  return (
                    <button
                      key={`page-${item.path}-${index}`}
                      type="button"
                      role="option"
                      data-index={index}
                      aria-selected={isActive}
                      onClick={() => selectPath(item.path)}
                      onMouseEnter={() => {
                        const selectableIdx = selectableRows.findIndex((r) => r.index === index);
                        if (selectableIdx >= 0) setHighlightIndex(selectableIdx);
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
                        isActive ? cn(colors.surfaceHover, colors.primaryFg) : colors.text,
                      )}
                    >
                      {item.icon ? (
                        <span className={cn('flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg', isActive ? colors.surfaceHover : 'bg-black/5 dark:bg-white/5')}>
                          {item.icon}
                        </span>
                      ) : (
                        <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-black/5 dark:bg-white/5">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </span>
                      )}
                      <span className="flex-1 truncate font-medium">{item.label}</span>
                      {item.group && (
                        <span className={cn('text-xs truncate shrink-0', colors.textMuted)}>{item.group}</span>
                      )}
                    </button>
                  );
                }

                return (
                  <button
                    key={`record-${item.path}-${index}`}
                    type="button"
                    role="option"
                    data-index={index}
                    aria-selected={isActive}
                    onClick={() => selectPath(item.path)}
                    onMouseEnter={() => {
                      const selectableIdx = selectableRows.findIndex((r) => r.index === index);
                      if (selectableIdx >= 0) setHighlightIndex(selectableIdx);
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors border-t',
                      colors.borderLight,
                      isActive ? cn(colors.surfaceHover, colors.primaryFg) : colors.text,
                    )}
                  >
                    <span className={cn('flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg', colors.textMuted)}>
                      {GLOBAL_SEARCH_TYPE_ICONS[item.typeLabel] ?? GLOBAL_SEARCH_DEFAULT_ICON}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{item.label}</p>
                      {item.subtitle && (
                        <p className={cn('text-xs truncate', colors.textMuted)}>{item.subtitle}</p>
                      )}
                      <p className={cn('text-[11px]', colors.textMuted)}>{item.typeLabel}</p>
                    </div>
                  </button>
                );
              })}
              {recordsLoading && (
                <div className={cn('px-4 py-2 text-xs', colors.textMuted, 'border-t', colors.borderLight)}>
                  Searching records…
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CombinedSearch;
